import { ApolloError } from '@apollo/client';
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  usePrevious,
  Text,
} from '@chakra-ui/react';
import TableSharpIcon from '@gamma/ui/icons/TableSharpIcon';
import ViewListSharpIcon from '@gamma/ui/icons/ViewListSharpIcon';
import {
  Board,
  Column,
  FlushPanelContent,
  Panel,
  PanelHeader,
  PanelHeading,
} from '@gamma/ui/layout';
import { ErrorBoundary } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  useDateRangeContext,
  useDateRangeQueryString,
  useManualPagination,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { PaginatedAlertEntityName } from '../queries';
import moment from 'moment';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { SortingRule } from 'react-table';
import {
  AuthContext,
  SlideDrawerContext,
} from '@investigator/shared/providers';
import {
  DetectionsFilters,
  DetectionsListView,
  DetectionsPageHeader,
  DetectionsSort,
  DetectionsTableView,
} from '../components';
import { useDetectionContext } from '../context';
import { DataTablePageSize } from '@gamma/ui/data-table';
import { Detection } from '@investigator/shared/generated/graphql';
import {
  DateSelectSync,
  getDateRangeOptionFromDates,
} from '@investigator/shared/components';
import RefreshSharpIcon from '@gamma/ui/icons/RefreshSharpIcon';

export const formatSort = (value: string) => {
  if (value === 'latest_start_timestamp.desc') {
    return [{ id: 'latest_start_timestamp', desc: true }];
  } else if (value === 'latest_start_timestamp.asc') {
    return [{ id: 'latest_start_timestamp', desc: false }];
  } else if (value === 'rank.severity.desc') {
    return [{ id: 'rank.severity', desc: true }];
  } else if (value === 'rank.severity.asc') {
    return [{ id: 'rank.severity', desc: false }];
  }
  return [{ id: 'latest_start_timestamp', desc: true }];
};

interface ApiSortOption {
  sort_by: string;
  sort_dir: string;
}

export interface EntityOption {
  value: string;
  label: string;
  type: string;
}

export interface DetectionQueryVariables {
  query: string;
  size: number;
  offset: number;
  sort?: ApiSortOption[];
  sortBy?: string;
}

const { controls } = i18n.pages.detections;
const { refresh, refreshTriagePageToolTip } = i18n.dateSelectSync;

export type SantizedDetection = Detection & {
  'rank.severity'?: number;
  'alert_info.alert_name'?: string;
  'alert_info.alert_type'?: string;
  'alert_entity.entity_name'?: string;
  'rank.is_custom_severity'?: boolean;
  'tenant_info.tenant_display_name'?: string;
  'assignment_info.assigned_to_user_alias'?: string;
  'escalation_data.escalation_status'?: boolean | undefined;
  paginated_source_entities?: PaginatedAlertEntityName;
  paginated_destination_entities?: PaginatedAlertEntityName;
  detection_count?: number;
  first_seen?: number;
  severity?: number;
  total_detections?: number;
  notes_count?: number;
};

export const Detections = () => {
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const { setPortalType, setIsSlideDrawerOpen } =
    useContext(SlideDrawerContext);

  const { user, authState } = useContext(AuthContext);
  const { selectedItem, setSelectedItem, detectionsData, queryVariables } =
    useDetectionContext();

  const { dateRange } = useDateRangeContext();

  const navigate = useNavigate();
  const { start, end } = useDateRangeQueryString();
  const { pathname } = useLocation();

  // Read from URL as single source of truth - no local state
  const activeView = params.get('view') || 'list';
  const detection_id = params.get('detection_id');
  const sortByParam = params.get('sort_by') || 'latest_start_timestamp.desc';

  const [detectionsError, setDetectionsError] = useState<
    ApolloError | undefined
  >();
  const [detectionsLoading, setDetectionsLoading] = useState<boolean>(true);

  const [searchParams, setSearchParams] = useSearchParams();
  const setDetectionsParam = useCallback(
    (k: string, val: string) => {
      if (user && authState && params.get(k) !== val) {
        const newParams = new URLSearchParams(params);
        if (val === '') {
          newParams.delete(k);
        } else {
          newParams.set(k, val);
        }
        setSearchParams(newParams);
      }
    },
    [params, user, authState, setSearchParams],
  );

  const prevSelectedItem = usePrevious(selectedItem);
  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    if (prevSelectedItem !== selectedItem && selectedItem) {
      newParams.set('detection_id', selectedItem.detection_id);
      newParams.delete('refetch_main');
      navigate(`?${newParams.toString()}`, { replace: true }); // Update the URL with detection_id
    }
  }, [prevSelectedItem, selectedItem, navigate]); // Removed searchParams from dependencies

  const rawStart = searchParams.get('start');
  const rawEnd = searchParams.get('end');

  const defaultStart = moment().subtract(7, 'days').unix();
  const defaultEnd = moment().unix();

  useEffect(() => {
    if (!rawStart || !rawEnd) {
      // Logic to set default start and end dates in the URL as needed
    }
  }, [rawStart, rawEnd, defaultStart, defaultEnd]);

  const {
    offset,
    pageSize,
    pageIndex,
    pageCount,
    onFetchData,
    getPageCount,
    getSortsForAPI,
    resetPagination,
  } = useManualPagination({
    sortBy: formatSort(queryVariables?.sortBy ?? ''),
    pageIndexParam: params.get('pg')
      ? (Number(params.get('pg')) as number)
      : undefined,
    initPageSize: params.get('pg_size')
      ? (Number(params.get('pg_size')) as DataTablePageSize)
      : undefined,
  });

  useEffect(() => {
    const handleDetectionSelection = () => {
      const detections = detectionsData?.queryDetectionsPaginated?.detections;
      if (!detections) return;

      // Check if page data is synchronized
      const actualOffset = detectionsData.queryDetectionsPaginated.offset;
      if (actualOffset !== pageIndex * pageSize) return;

      // Clear selection if no detections
      if (detections.length === 0) {
        setSelectedItem(undefined);
        return;
      }

      // Select the detection
      const selectDetection = (detection: Detection) => {
        // Only update if it's actually different
        if (selectedItem?.detection_id !== detection.detection_id) {
          setSelectedItem(detection);
        }
      };

      // Select the first detection
      const selectFirstDetection = () => {
        const firstDetection = detections[0];
        if (firstDetection) {
          selectDetection(firstDetection);
        }
      };

      // If no detection_id specified, select first
      if (!detection_id) {
        selectFirstDetection();
        return;
      }

      // Find the requested detection
      const currentDetection = detections.find(
        (detection: Detection) => detection.detection_id === detection_id,
      );

      // If found and on current page, select it; otherwise select first
      if (currentDetection) {
        const detectionIndex = detections.findIndex(
          (d: Detection) => d.detection_id === currentDetection.detection_id,
        );

        // Check if the detection is on the current page
        const isOnCurrentPage =
          detectionIndex >= 0 && detectionIndex < pageSize;
        if (isOnCurrentPage) {
          selectDetection(currentDetection);
        } else {
          selectFirstDetection();
        }
      } else {
        selectFirstDetection();
      }
    };

    handleDetectionSelection();
  }, [detection_id, detectionsData, pageSize, pageIndex]);

  const refreshButtonOnClick = () => {
    const range = getDateRangeOptionFromDates(start, end) ?? dateRange;
    if (!range) return;

    const [amount, unit] = range;
    const newEnd = `${moment().unix()}`;
    const newStart = `${moment().subtract(amount, unit).unix()}`;
    params.set('end', newEnd);
    params.set('start', newStart);

    navigate({
      pathname,
      search: params.toString(),
    });
  };

  const handleFetchData = useCallback(
    (
      pageIndex: number,
      pageSize: number,
      sortBy?: SortingRule<SantizedDetection>[],
    ) => {
      if (sortBy?.length && activeView === 'table') {
        setDetectionsParam(
          'sort_by',
          `${sortBy[0]?.id}.${sortBy[0]?.desc ? 'desc' : 'asc'}`,
        );
      }
      onFetchData(pageIndex, pageSize, sortBy ?? []);
    },
    [activeView, onFetchData, setDetectionsParam],
  );

  // Track dateRange changes for forcing DataTable reset
  const [dateRangeChangeKey, setDateRangeChangeKey] = useState(0);

  const onDateRangeChange = () => {
    // Reset to first page on date range change
    setDetectionsLoading(true);
    handleFetchData(0, pageSize, undefined);
    setDateRangeChangeKey((k) => k + 1); // increment key to force DataTable remount
  };

  useEffect(() => {
    if (activeView === 'list') {
      setIsSlideDrawerOpen?.(false);
    }
  }, [activeView, setIsSlideDrawerOpen]);

  useEffect(() => {
    setPortalType?.('detection');
    // TODO: Fix portal issues and not hack mount
  }, [setPortalType]);

  useEffect(() => {
    if (dateRange) {
      resetPagination();
      handleFetchData(0, pageSize, formatSort(sortByParam));
    }
  }, [dateRange]);

  useEffect(() => {
    if (detectionsData?.queryDetectionsPaginated?.total_items === 0) {
      setIsSlideDrawerOpen?.(false);
    }
  }, [detectionsData, setIsSlideDrawerOpen]);

  if (detectionsError) {
    return (
      <GraphQLReqStatus
        isBackground={true}
        error={detectionsError}
        loading={detectionsLoading}
      />
    );
  }

  return (
    <ErrorBoundary page="Detections" styleClass="main">
      <Box mb={0} data-testid="detections-wrapper">
        <DetectionsPageHeader
          isLoading={detectionsLoading}
          queryVariables={queryVariables}
          onDateRangeChange={onDateRangeChange}
        />
        <Board>
          <Column data-testid="detections-filters">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              w="100%"
            >
              <DetectionsFilters
                size={pageSize}
                offset={offset}
                formatSort={formatSort}
                getPageCount={getPageCount}
                getSortsForAPI={getSortsForAPI}
                setDetectionsError={setDetectionsError}
                setDetectionsLoading={setDetectionsLoading}
              />
              <Box ml={4} flexShrink={0}>
                <DateSelectSync
                  navigate={navigate}
                  dateSelectButtonConfig={{
                    iconButtonProps: {
                      'aria-label': refreshTriagePageToolTip,
                      colorScheme: 'gray',
                      borderRadius: '4px 0px 0px 4px',
                      variant: 'solid',
                      icon: <RefreshSharpIcon />,
                      onClick: refreshButtonOnClick,
                    },
                    toolTipProps: {
                      label: (
                        <Text data-testid="refresh-button-refresh-text">
                          {refresh}
                        </Text>
                      ),
                      'aria-label': refreshTriagePageToolTip,
                      placement: 'top',
                    },
                  }}
                />
              </Box>
            </Box>
          </Column>
          <Column>
            <Panel>
              <PanelHeader>
                <PanelHeading>
                  <HStack
                    divider={
                      activeView === 'list' ? <Box height="24px" /> : <span />
                    }
                  >
                    <ButtonGroup isAttached>
                      <Button
                        size="sm"
                        variant="solid"
                        onClick={() => setDetectionsParam('view', 'list')}
                        leftIcon={<ViewListSharpIcon />}
                        colorScheme={activeView === 'list' ? 'blue' : 'gray'}
                      >
                        {controls.list}
                      </Button>
                      <Button
                        size="sm"
                        variant="solid"
                        leftIcon={<TableSharpIcon />}
                        onClick={() => setDetectionsParam('view', 'table')}
                        colorScheme={activeView === 'table' ? 'blue' : 'gray'}
                      >
                        {controls.table}
                      </Button>
                    </ButtonGroup>
                    <DetectionsSort
                      params={params}
                      setDetectionsParam={setDetectionsParam}
                      activeView={activeView}
                      sortByParam={sortByParam}
                    />
                  </HStack>
                </PanelHeading>
              </PanelHeader>
              <FlushPanelContent>
                {activeView === 'list' && (
                  <DetectionsListView
                    offset={offset}
                    params={params}
                    pageSize={pageSize}
                    pageIndex={pageIndex}
                    pageCount={pageCount}
                    formatSort={formatSort}
                    getSortsForAPI={getSortsForAPI}
                    handleFetchData={handleFetchData}
                    detectionsLoading={detectionsLoading}
                  />
                )}
                {activeView === 'table' && (
                  <DetectionsTableView
                    offset={offset}
                    params={params}
                    sort_by={sortByParam}
                    pageSize={pageSize}
                    pageIndex={pageIndex}
                    pageCount={pageCount}
                    formatSort={formatSort}
                    getSortsForAPI={getSortsForAPI}
                    handleFetchData={handleFetchData}
                    detectionsLoading={detectionsLoading}
                    dateRangeChangeKey={String(dateRangeChangeKey)}
                  />
                )}
              </FlushPanelContent>
            </Panel>
          </Column>
        </Board>
      </Box>
    </ErrorBoundary>
  );
};
